import {
  type SugarAttendanceFunding,
  type SugarEnrollment,
  type SugarEnrollmentFunding,
  type SugarEnrollmentItem,
  type SugarRosterFundingItem,
  type SugarRosterFundingValues,
  type SugarScholarshipConfirmResponse,
  type SugarScholarshipConfirmResult
} from '@app/types'
import { type MantineTheme } from '@mantine/core'

import { formatCurrency } from './formatCurrency'

// === THE WIRE BOUNDARY ===
// The only place that encodes backend funding tokens, verified against the
// MIQ-2535 endpoint in dpp-sugarcrm PR #631 (feature/MIQ-2535-clean).
export const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' }
]

// Unlike the Yes/No fields, the server matches these values case-sensitively.
export const UPK_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: '15 Hours', value: 'upk_15hr' },
  { label: '30 Hours', value: 'upk_30hr' }
]

// SFP mirrors UPK's shape but not its tiers: DPS allocates SFP in slots of 10
// funded hours/week. Case-sensitive, same as UPK (MIQ-2661 validateSfpType).
export const SFP_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: '1 Slot (10 hrs/wk)', value: 'sfp_10hr' },
  { label: '2 Slots (20 hrs/wk)', value: 'sfp_20hr' }
]

// === FUNDING FORM HELP COPY & PLACEHOLDERS ===
// Verbatim from the MIQ-2651 v3 mockup TIPS block; the column labels are
// abbreviated, so this is where "slot" is actually defined for the provider.
export const SFP_HELP_LABEL =
  'SFP refers to State Funded Preschool. Your site partners with Denver Public Schools to offer UPK funding for qualified 3-year-olds. This is NOT to be used for traditional 4-year-old UPK funding. Please use the specified UPK dropdown if your student is 4. Slots: 1 Slot = 10 hours of funding/week; 2 Slots = 20 hours of funding/week.'

export const CCCAP_HELP_LABEL =
  'CCCAP refers to the Colorado Child Care Assistance Program. Along with Head Start, CCCAP affects DPP scholarship eligibility — a student receiving CCCAP funding cannot also hold an active DPP scholarship.'

export const HEAD_START_HELP_LABEL =
  'This column appears because your site is a Head Start provider. Along with CCCAP, Head Start affects DPP scholarship eligibility — a student receiving Head Start funding cannot also hold an active DPP scholarship.'

export const UPK_HELP_LABEL =
  'The hours of Universal Preschool Colorado funding this student receives: None, 15 hours, or 30 hours. UPK does not affect scholarship eligibility, but is factored into DPP award amounts.'

// Verbatim from the MIQ-2458 V3 mockup TIPS block (2026-05-18), the design of
// record for the attendance funding form, except where round-1 testing revised
// the copy: Other Funding's closing sentence and the whole Cap Request text are
// the program lead's, verbatim from her MIQ-2345 comment of 2026-09-18 (MIQ-2923).
// The roster labels above cover CCCAP, Head Start and UPK; these five are the
// attendance sheet's own.
export const OTHER_FUNDING_HELP_LABEL =
  'Refers to privately sourced financial support with no public or government funding involved. This includes institution-administered scholarships, internal grants, employer-sponsored subsidies, or other non-public financial assistance provided directly by the organization. If not receiving other funding, enter $0'

export const FAMILY_OUT_OF_POCKET_HELP_LABEL =
  'Refers to the average monthly amount the family paid out of pocket after outside funding or financial assistance has been applied. If the family pays $0, please enter $0'

export const PUBLISHED_TUITION_RATE_HELP_LABEL =
  'The published tuition rate refers to the maximum full, undiscounted rate your program publicly advertises. This is the rate THIS unique family would be charged prior to any scholarships, sliding scale adjustments, or financial assistance being applied.'

export const CAP_REQUEST_HELP_LABEL =
  'Enter the TOTAL MONTHLY amount you want disbursed by DPP for this specific student. Tuition caps are typically requested when the total amount of funding collected per child from all funding sources combined exceeds your monthly published tuition rate. Requesting a cap only affects your DPP funding and is used to help layer funding streams in accordance with DPP policy.'

// Note there is no separate attendance SFP string: V3's SFP tooltip is the
// roster's SFP_HELP_LABEL without its trailing slots sentence, and the
// roster's is the superset. Reuse SFP_HELP_LABEL.

// MIQ-2733: an empty box on a row that owes an answer says so in brand red, where
// an exempt row keeps the neutral N/A. Red placeholder text, deliberately not
// Mantine's `error` state: a sheet opened for the first time has every box empty,
// and a grid of red borders reads as forty things gone wrong rather than forty
// things still to do.
const REQUIRED_PLACEHOLDER_STYLES = (theme: MantineTheme) => ({
  input: { '&::placeholder': { color: theme.colors['brand-red'][6] } }
})

// Shared by the roster's selects and the attendance sheet's selects and currency
// inputs, so the same empty-but-owed box looks the same on both screens.
export const fundingPlaceholderProps = (required: boolean, value: string | number | null) =>
  required && (value === null || value === '')
    ? { placeholder: 'Required', styles: REQUIRED_PLACEHOLDER_STYLES }
    : { placeholder: 'N/A' }

// MIQ-2921: the amount behind the attendance sheet's "Same as ..." control, the
// only reader of the MIQ-2920 hint (dpp-sugarcrm PR #762). The contract is a
// number above zero or null, always present; an environment that predates #762
// omits the key. Anything else -- zero, a string, a non-finite number -- means
// "no prior request", so a malformed value never becomes an amount a provider
// can copy into a payment-affecting field with one click.
export const priorRequestedCap = (funding: SugarAttendanceFunding | undefined): number | null => {
  const value = funding?.priorRequestedCap

  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
}

// The collapsed tri-state formatGapStatus emits, alongside the raw gap_status_c
// key the roster now also carries. 'No' is the backend's default case -- it
// absorbs ineligible, rescinded, waitlisted, site-not-participating and
// never-applied -- which is why the roster's Scholarship column and its pill
// read the raw status instead. This stays for the consumers that genuinely want
// the tri-state: AttendanceTable's DPP Scholarship label and its pre-2026/27
// required rules, and EnrollmentViewModal. Neither the roster's funding lock nor
// the attendance sheet's Paying $0 rule is one of them -- they read the raw
// status through isScholarshipLocked and payingZeroRequired below.
export type ScholarshipStatus = 'No' | 'Pending' | 'Yes'

// A null status cannot actually arrive -- formatGapStatus returns 'No' for
// everything it does not recognise -- but collapsing it the same way is what
// stops one student being labelled twice. Narrowed rather than passed through:
// the value is typed as a closed union but arrives as untyped JSON.
export const toScholarshipStatus = (status: SugarEnrollment['gapScholarshipStatus'] | undefined): ScholarshipStatus =>
  status === 'Yes' || status === 'Pending' ? status : 'No'

// MIQ-2745: freeze-at-confirmation (Erin's decision, MIQ-2699). Both statuses
// lock all four funding columns; every other status leaves them editable. The
// set started as CCCAP and Head Start, which contradict a scholarship outright,
// and MIQ-2701 added UPK and SFP, which do not -- they coexist as deductions,
// but recalculating an award DPP has already approved is what the lock exists
// to stop. Erin chose under_dpp_review over active as the trigger on
// MIQ-2701 (comment 26678), matching the CCCAP rule already here.
//
// Reads the raw gap status, not the lossy tri-state above -- 'Yes' only covers
// active, and the tri-state's 'Pending' also covers awaiting_your_confirmation,
// which must stay editable.
export const SCHOLARSHIP_LOCKED_STATUSES: readonly string[] = ['active', 'under_dpp_review']

// Derived on demand from the live enrollment, never stored on the form row.
// Form state holds what the provider typed; the lock is server truth, and a
// copy of it taken when the form was seeded goes stale the moment DPP activates
// a scholarship in Sugar. The roster does not reseed for that: its reseed keys
// on the set of enrollment ids, so a refetch that changes only gap_status_c
// leaves every form row untouched, and a cached flag would leave the boxes
// editable under a badge already reading Active (MIQ-2702).
export const isScholarshipLocked = (status: string | null): boolean =>
  status !== null && SCHOLARSHIP_LOCKED_STATUSES.includes(status)

// MIQ-2954: the attendance sheet asks "DPP Family Paying $0" of an active
// scholarship only. Mirrors FundingFieldValidator::payingZeroRequired() on the
// server (MIQ-2953), which narrowed the rule from active-or-pending on the
// program lead's 2026-09-21 feedback. Raw status, not the tri-state: a Pending
// row keeps displaying as Pending but no longer owes the answer. Drives the
// control's visibility, its required rule, the Family Out of Pocket agreement
// check, and whether the answer is posted at all.
export const payingZeroRequired = (status: string | null): boolean => status === 'active'

// MIQ-2745: two locked statuses, two reasons. The tooltip only shows while the
// row is locked (disabled={!scholarshipLocked}), so anything else falls back
// to the active text rather than needing its own label.
//
// MIQ-2702 adds the remedy. Every locked box says how to get it changed, since
// a provider hovering one of them is asking exactly that, and the row-level
// note below is easy to miss on a table this wide.
export const scholarshipDisabledLabel = (status: string | null): string =>
  status === 'under_dpp_review'
    ? 'Disabled — scholarship confirmation is under DPP review. Contact DPP to change.'
    : 'Disabled — student has an active scholarship. Contact DPP to change.'

// MIQ-2702: the visible half of scholarshipDisabledLabel. A disabled input takes no
// pointer events, so a tooltip is unreachable on touch and never enters the
// accessibility tree -- this line is the one form of the lock that every provider
// actually gets. The roster shows it once per student in the Scholarship cell; the
// attendance sheet shows it under CCCAP and Head Start (MIQ-2923), the program
// lead asking for the roster's wording on both screens.
//
// under_dpp_review keeps the reassurance it has carried since MIQ-2745: "Under
// DPP Review" alone does not tell a provider their submitted answers survived
// the lock.
export const scholarshipLockedNote = (status: string | null): string =>
  status === 'under_dpp_review'
    ? 'Locked — your submission is preserved. Contact DPP to change funding.'
    : 'Locked — contact DPP to change funding.'

export type FundingFormRow = {
  enrollmentId: string
  ccapEnrolled: string | null
  headStart: string | null
  sfpEnrolled: string | null
  upkType: string | null
  fundingRequired: boolean
  headStartVisible: boolean
  sfpVisible: boolean
}

export const toFormRow = (enrollment: SugarEnrollment, funding?: SugarEnrollmentFunding | null): FundingFormRow => ({
  enrollmentId: enrollment.id,
  ccapEnrolled: funding?.ccapEnrolled ?? null,
  headStart: funding?.headStart ?? null,
  sfpEnrolled: funding?.sfpEnrolled ?? null,
  upkType: funding?.upkType ?? null,
  // MIQ-2706: only students actually enrolled at the site owe funding answers.
  // The other three enrollment statuses -- pending, site_claim_pending and
  // inactive -- are exempt, which is also what keeps the roster's virtual
  // pending rows out of the count: they arrive with status 'pending'.
  //
  // This is the ENROLLMENT status, not the scholarship one. The two columns
  // share the word "Active" and sit two apart on the row, and only this one has
  // an Inactive and a Pending to go with it (Erin Cooper, MIQ-2111 2026-07-30).
  fundingRequired: enrollment.status === 'active',
  headStartVisible: funding?.headStartVisible ?? false,
  sfpVisible: funding?.sfpVisible ?? false
})

// Every funding question this row actually puts to the provider, answered.
// Both halves of "actually" are load-bearing. A column the site does not have is
// not a question -- headStartVisible / sfpVisible decide that, and the ticket's
// CCCAP+UPK rule would report a Head Start or SFP site as complete with two
// columns sitting empty. Neither is a box that renders disabled, and as of
// MIQ-2702 that is every box on the row: confirming a scholarship
// (under_dpp_review) or activating it locks CCCAP and Head Start because a
// student receiving either cannot hold a scholarship (MIQ-2745), and locks UPK
// and SFP because they are deductions against an award that is either already
// approved or sitting in front of DPP for the decision (MIQ-2701) -- the lock
// starts at under_dpp_review, so "approved" is only half of what it protects.
// Counting a locked empty box as unanswered would leave any site with a
// confirmed scholarship permanently short of complete, with nothing a provider
// could do about it.
//
// Deliberately says nothing about WHICH rows have to be complete -- that is
// fundingRequired, and the program-year gate on top of it -- so that the same
// predicate can drive the banner, the progress counter and the per-box marking.
//
// scholarshipLocked is a parameter rather than a field on the row so that this
// and the controls it describes read one live source: see isScholarshipLocked.
export const isFundingComplete = (row: FundingFormRow | undefined, scholarshipLocked: boolean): boolean => {
  if (!row) {
    return false
  }

  // Nothing on a locked row is editable, so it can owe nothing. Replaces the
  // per-field exemptions this predicate carried while only two of the four
  // columns locked.
  if (scholarshipLocked) {
    return true
  }

  return (
    Boolean(row.ccapEnrolled) &&
    Boolean(row.upkType) &&
    (!row.headStartVisible || Boolean(row.headStart)) &&
    (!row.sfpVisible || Boolean(row.sfpEnrolled))
  )
}

export const toUpsertItem = (row: FundingFormRow): SugarRosterFundingItem => {
  // CCCAP and UPK are always writable. Head Start and SFP are gated on the
  // site's stream toggle before their value is read, so sending them to a site
  // without that stream rejects the whole row — omit them instead.
  const funding: SugarRosterFundingValues = {
    ccapEnrolled: row.ccapEnrolled,
    upkType: row.upkType
  }

  if (row.headStartVisible) funding.headStart = row.headStart
  if (row.sfpVisible) funding.sfpEnrolled = row.sfpEnrolled

  return {
    enrollmentId: row.enrollmentId,
    funding
  }
}

// MIQ-2684 exposes the raw gap_status_c key alongside the lossy tri-state.
// Only this one value changes behaviour; every other status is display-only,
// which is why it is the single key named here rather than a full enum.
export const AWAITING_CONFIRMATION = 'awaiting_your_confirmation'

// Three conditions, all of them required -- an awaiting row is NOT always
// actionable. The status must be awaiting_your_confirmation: the API also
// permits re-confirming an active scholarship (latest-value-wins), but the
// ticket requires the form not render for Active, so that path stays
// server-side. Gated program years are excluded because /scholarship/confirm
// rejects them outright — offering the button there would only produce a
// guaranteed error. And MIQ-2845 adds the third: the saved funding row has to
// be complete, so the badge cannot open the confirm modal while a required
// funding answer is still blank. That one reads the SAVED row, not the live
// form values -- MIQ-2884's server guard validates what is on file, and
// confirming succeeds or fails against that same row, so the UI gate has to
// agree with it rather than with unsaved keystrokes.
export const isConfirmable = (enrollment: SugarEnrollment, isGated: boolean, saved?: FundingFormRow): boolean =>
  !isGated &&
  enrollment.gapStatus === AWAITING_CONFIRMATION &&
  isFundingComplete(saved, isScholarshipLocked(enrollment.gapStatus))

// Narrows the confirm response's `data` to the success result. The endpoint can
// answer with a third shape -- MIQ-2884's incomplete-funding rejection carries
// { missingFields } and nothing else -- so a non-null `data` is not on its own
// proof the confirm succeeded. Structural rather than a `success` test, because
// it is meant to compose with one the caller has already made.
export const isScholarshipConfirmResult = (
  data: SugarScholarshipConfirmResponse['data']
): data is SugarScholarshipConfirmResult => data !== null && 'status' in data

export type StreamConflict = {
  stream: string
  // True when the value is already saved. A stored Yes makes the confirm fail
  // server-side; an unsaved Yes lets it succeed and is discarded. The modal
  // needs to tell the provider which of those is about to happen.
  stored: boolean
}

// CCCAP and Head Start are the two streams that contradict the attestation --
// UPK and SFP never block a scholarship. Reads the live form row rather than
// the payload so an answer typed but not yet saved still warns.
export const conflictingStreams = (row?: FundingFormRow, baseline?: FundingFormRow): StreamConflict[] => {
  if (!row) return []

  const conflicts: StreamConflict[] = []

  if (row.ccapEnrolled === 'Yes') {
    conflicts.push({ stream: 'CCCAP', stored: baseline?.ccapEnrolled === 'Yes' })
  }

  // Gated on visibility for the same reason toUpsertItem omits it: a site
  // without the Head Start stream has no meaningful value in that field.
  if (row.headStartVisible && row.headStart === 'Yes') {
    conflicts.push({ stream: 'Head Start', stored: baseline?.headStart === 'Yes' })
  }

  return conflicts
}

// === RAW STATUS TAXONOMY ===
// gap_status_c carries eight keys. These two groupings no longer drive the
// status views -- those partition on Application Status as of MIQ-2660's QA
// pass -- but they still order the Scholarship column, via rosterSort's
// SCHOLARSHIP_RANKS. provider_not_participating, inactive and a null status stay
// outside both groups and sort last. active is outside both groups too, but it
// ranks first via its own predicate in SCHOLARSHIP_RANKS.
export const SCHOLARSHIP_PENDING_STATUSES: readonly string[] = [AWAITING_CONFIRMATION, 'under_dpp_review', 'waitlist']
export const SCHOLARSHIP_DENIED_STATUSES: readonly string[] = ['ineligible', 'rescinded']

// === STATUS PILL PRESENTATION ===
// Colour only -- the label is NOT here. It comes from options.scholarshipStatus
// so a picklist rename needs no frontend edit, which is the same reason the
// column has never carried a hardcoded label map. Values are Mantine theme keys
// rather than the mockup's hex.
//
// The mockup pairs each pill with a tooltip; those are not adopted. Five of the
// six restated the label the badge already shows, and no other badge in this app
// carries one. The sixth said something the label does not -- that a locked
// submission is preserved -- and that is rendered as visible text under the
// badge instead.
const SCHOLARSHIP_BADGE_COLORS: Record<string, string> = {
  // Green, not the mockup's yellow, so an active scholarship reads the same as
  // an active enrollment -- EnrollmentStatusBadge renders that green two columns
  // to the left, and two badges on one row meaning "this is the good state"
  // should not disagree about what colour that is.
  active: 'green',
  [AWAITING_CONFIRMATION]: 'orange',
  ineligible: 'red',
  rescinded: 'gray',
  under_dpp_review: 'gray',
  waitlist: 'gray'
}

// D3: any non-null status has to render, so provider_not_participating, inactive
// and any future picklist value fall back to a neutral badge carrying the
// server's own label rather than disappearing. The mockup maps six of the eight
// keys and would drop the other two silently.
export const scholarshipBadgeColor = (status: string): string => SCHOLARSHIP_BADGE_COLORS[status] ?? 'gray'

// === APPLICATION STATUS TAXONOMY ===
// The status views partition on Application Status, not the DPP Scholarship
// Status (MIQ-2660 QA, comment 25488). They are different fields about
// different things -- a student can hold an approved application and no
// scholarship. The view labels were always application vocabulary:
// application_status_list carries `approved`, `pending` and `denied` verbatim,
// while the scholarship list has neither `approved` (it is `active`) nor
// `denied` at all, which MIQ-2543 retired in favour of `ineligible`.
//
// Unlike the scholarship taxonomy, every real picklist value buckets here, so
// the three counts fall short of the All count only for a student with no
// application on file (F2).
export const APPROVED_APPLICATION_STATUSES: readonly string[] = ['approved', 'approved_new_school_year']
export const PENDING_APPLICATION_STATUSES: readonly string[] = ['pending', 'waitlist']
export const DENIED_APPLICATION_STATUSES: readonly string[] = ['denied']

// A cleared Sugar enum persists as '' rather than NULL, and nestRosterItem does
// not normalize this field the way MIQ-2684 taught it to for gapStatus. Both
// values mean "no application status", so they collapse once here rather than
// being guarded at each call site.
export const applicationStatusOf = (item: SugarEnrollmentItem): string | null => item.application.status || null

export type RosterView = 'all' | 'approved' | 'denied' | 'pending'

export type RosterViewDefinition = {
  label: string
  match: (status: string | null) => boolean
  value: RosterView
}

// Order is load-bearing: the tabs render in this sequence, the counts are read
// back positionally, and rosterSort derives the Application Status column's rank
// from these indexes, so the column and the tabs above it cannot drift.
export const APPLICATION_VIEWS: readonly RosterViewDefinition[] = [
  { label: 'All Students', match: () => true, value: 'all' },
  {
    label: 'Approved',
    match: (status) => status !== null && APPROVED_APPLICATION_STATUSES.includes(status),
    value: 'approved'
  },
  {
    label: 'Pending',
    match: (status) => status !== null && PENDING_APPLICATION_STATUSES.includes(status),
    value: 'pending'
  },
  {
    label: 'Denied',
    match: (status) => status !== null && DENIED_APPLICATION_STATUSES.includes(status),
    value: 'denied'
  }
]

// 'all' is index 0 and matches everything, so a view outside the union -- which
// the type makes unreachable -- degrades to showing the whole roster rather than
// an empty table.
export const matchesRosterView = (view: RosterView, status: string | null): boolean =>
  (APPLICATION_VIEWS.find(({ value }) => value === view) ?? APPLICATION_VIEWS[0]).match(status)

// Colour only -- the label comes from options.applicationStatus so a picklist
// rename needs no frontend edit (F8). Green matches EnrollmentStatusBadge's
// active green and the scholarship pill's, so badges on one row meaning "good
// state" agree about the colour. Waitlist stays grey to match
// SCHOLARSHIP_BADGE_COLORS.waitlist: the same word is the same colour in both
// status columns, and the tab already communicates the grouping (F9).
const APPLICATION_BADGE_COLORS: Record<string, string> = {
  approved: 'green',
  approved_new_school_year: 'green',
  denied: 'red',
  pending: 'orange',
  waitlist: 'gray'
}

// Any unmapped key -- a future picklist value -- renders neutral carrying the
// server's own label rather than disappearing.
export const applicationBadgeColor = (status: string): string => APPLICATION_BADGE_COLORS[status] ?? 'gray'

// MIQ-2794: the roster's `tuitionCredit` is Sugar's real_tuition_credit_c --
// the effective monthly credit, calculated as
// `ifElse($override_tuition_credit_c == 1, $tuition_override_amount_c, $tuition_credit_c)`.
// Shown only where Erin asked for it: an approved application whose sub-status
// says the student is actually taking the credit. Only `approved` counts, not
// `approved_new_school_year` (MIQ-2047): next year's scale is not approved yet,
// so that amount is not final. The roster row, the modal and the CSV export all
// read this one predicate.
export const TUITION_CREDIT_SUB_STATUSES: readonly string[] = ['enrolled', 'incentive']

// Returns the formatted amount, or null when the row should show nothing.
// A zero credit renders nothing rather than "$0.00", matching
// rosterScholarshipAward below. Number('') is 0, so
// the same check also absorbs the empty-string case a cleared Sugar decimal
// leaves behind.
export const rosterTuitionCredit = (item: SugarEnrollmentItem): string | null => {
  if (applicationStatusOf(item) !== 'approved') {
    return null
  }

  if (!TUITION_CREDIT_SUB_STATUSES.includes(item.application.statusDetails ?? '')) {
    return null
  }

  const amount = Number(item.enrollment.tuitionCredit)

  return amount ? formatCurrency(amount) : null
}

// The scholarship award is shown only while the scholarship is active, and never
// as "$0.00". Pending, rescinded and every other status show nothing, even when
// Sugar still holds a figure. Shared by the roster's Scholarship column and the
// CSV export: the program lead asked for the export to follow the page's rule
// (MIQ-2688, 2026-09-16), and one predicate is what keeps the two from drifting --
// the tuition credit column drifted exactly that way when the export copied it.
export const rosterScholarshipAward = (item: SugarEnrollmentItem): string | null =>
  item.enrollment.gapStatus === 'active' && item.enrollment.gapAwardAmount
    ? formatCurrency(item.enrollment.gapAwardAmount)
    : null
